import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Collapse,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";
import CommentsPanel from "../../components/CommentsPanel";
import ContactedStatusControl from "../../components/ContactedStatusControl";
import LeadAssignmentPanel from "../../components/LeadAssignmentPanel";
import { LEAD_CONTEXTS } from "../../helpers/leadContexts";

const DRUG_PAGE_SIZE = 12;

function hasMeaningfulValue(value) {
  return (
    value !== undefined &&
    value !== null &&
    String(value).trim() !== "" &&
    String(value).trim().toLowerCase() !== "unknown"
  );
}

function formatValue(value) {
  return hasMeaningfulValue(value) ? String(value) : "Unknown";
}

function formatCurrency(value) {
  if (!hasMeaningfulValue(value) || Number.isNaN(Number(value))) {
    return "Unknown";
  }

  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(value));
}

function formatScore(value) {
  if (!hasMeaningfulValue(value) || Number.isNaN(Number(value))) {
    return "Unknown";
  }

  return Number(value).toFixed(1);
}

function formatCount(value) {
  return new Intl.NumberFormat("en-GB").format(Number(value || 0));
}

function formatBucketSummary(buckets) {
  if (!Array.isArray(buckets) || buckets.length === 0) {
    return "None";
  }

  return buckets
    .map((bucket) => `${formatCount(bucket?.count || 0)} ${bucket?.value || "Unknown"}`)
    .join(", ");
}

function getTierChipSx(tier) {
  if (tier === "Tier 1") {
    return {
      color: "#9a3412",
      backgroundColor: "#fff7ed",
      border: "1px solid rgba(249, 115, 22, 0.35)",
    };
  }

  if (tier === "Tier 2") {
    return {
      color: "#1d4ed8",
      backgroundColor: "#eff6ff",
      border: "1px solid rgba(59, 130, 246, 0.35)",
    };
  }

  return {
    color: "#475569",
    backgroundColor: "#f8fafc",
    border: "1px solid rgba(148, 163, 184, 0.35)",
  };
}

function normalizeExternalUrl(value) {
  if (!hasMeaningfulValue(value)) return "";

  const trimmedValue = String(value).trim();
  if (/^https?:\/\//i.test(trimmedValue)) return trimmedValue;
  return `https://${trimmedValue.replace(/^\/+/, "")}`;
}

function DetailTextRow({ label, value }) {
  return (
    <Typography sx={{ fontSize: "0.93rem", color: "#334155" }}>
      <strong>{label}:</strong> {value}
    </Typography>
  );
}

function DrugListItem({ drug }) {
  return (
    <Box
      sx={{
        border: "1px solid rgba(15, 23, 42, 0.08)",
        borderRadius: 2,
        backgroundColor: "#ffffff",
        px: 1.25,
        py: 1.1,
        display: "grid",
        gap: 0.75,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          justifyContent: "space-between",
        }}
      >
        <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
          {formatValue(drug.drugName)}
        </Typography>
        {!drug.active && (
          <Chip
            label="Inactive"
            size="small"
            sx={{
              color: "#7f1d1d",
              backgroundColor: "#fef2f2",
              border: "1px solid rgba(239, 68, 68, 0.25)",
            }}
          />
        )}
      </Box>

      <Box sx={{ display: "grid", gap: 0.35 }}>
        <Typography sx={{ fontSize: "0.9rem", color: "#334155" }}>
          <strong>Development Stage:</strong> {formatValue(drug.developmentStage)}
        </Typography>
        <Typography sx={{ fontSize: "0.9rem", color: "#334155" }}>
          <strong>Molecule Type:</strong> {formatValue(drug.moleculeType)}
        </Typography>
        <Typography sx={{ fontSize: "0.9rem", color: "#334155" }}>
          <strong>Therapeutic Areas:</strong> {formatValue(drug.therapyArea)}
        </Typography>
        <Typography sx={{ fontSize: "0.9rem", color: "#334155" }}>
          <strong>Drug Type:</strong> {formatValue(drug.drugType)}
        </Typography>
        {drug.drugRevenueYear != null && (
          <Typography sx={{ fontSize: "0.9rem", color: "#334155" }}>
            <strong>Drug Revenue {drug.drugRevenueYear} (USD m):</strong>{" "}
            {formatValue(drug.drugRevenueUsdM)}
          </Typography>
        )}
        <Typography sx={{ fontSize: "0.9rem", color: "#334155" }}>
          <strong>Route of Administration:</strong> {formatValue(drug.routeOfAdministration)}
        </Typography>
        <Typography sx={{ fontSize: "0.9rem", color: "#334155" }}>
          <strong>Geography:</strong> {formatValue(drug.drugGeography)}
        </Typography>
        <Typography sx={{ fontSize: "0.9rem", color: "#334155" }}>
          <strong>Target:</strong> {formatValue(drug.target)}
        </Typography>
        <Typography sx={{ fontSize: "0.9rem", color: "#334155" }}>
          <strong>ATC Classification:</strong> {formatValue(drug.atcClassification)}
        </Typography>
        <Typography sx={{ fontSize: "0.9rem", color: "#334155" }}>
          <strong>Drug Descriptor:</strong> {formatValue(drug.drugDescriptor)}
        </Typography>
        <Typography sx={{ fontSize: "0.9rem", color: "#334155" }}>
          <strong>Controlled Substance:</strong> {formatValue(drug.controlledSubstance)}
        </Typography>
      </Box>

      {hasMeaningfulValue(drug.indication) && (
        <Typography sx={{ fontSize: "0.9rem", color: "#475569", lineHeight: 1.55 }}>
          <strong>Indication:</strong> {drug.indication}
        </Typography>
      )}
    </Box>
  );
}

export default function GlobaldataCompanyCard({
  company,
  postJson,
  deleteJson,
  getApiJson,
  onOpenCrunchbaseMatch,
  onOpenCompanyHouseMatch,
  assignment,
  canManageAssignments = false,
  assignableUsers = [],
  assignableUsersLoading = false,
  onAssignmentsChanged,
  onContactedChanged,
}) {
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [detailPayload, setDetailPayload] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [drugsPayload, setDrugsPayload] = useState(null);
  const [drugsLoading, setDrugsLoading] = useState(false);
  const [drugsError, setDrugsError] = useState("");
  const [drugPage, setDrugPage] = useState(0);
  const [contactedLoading, setContactedLoading] = useState(false);
  const [contactedError, setContactedError] = useState("");

  const encodedCompanyName = useMemo(
    () => encodeURIComponent(company.companyName),
    [company.companyName]
  );
  const matchedCrunchbaseCompanyName =
    company.matchedCrunchbaseCompanyName || company.companyName;
  const matchedCompanyHouseCompanyName =
    company.matchedCompanyHouseCompanyName || company.companyName;
  const isContacted = Boolean(company.contacted);

  const loadDetails = useCallback(async () => {
    setDetailLoading(true);
    setDetailError("");

    try {
      const payload = await getApiJson(
        `/api/globaldata/company-details?companyName=${encodedCompanyName}`
      );
      setDetailPayload(payload);
    } catch (error) {
      console.error("Unable to load GlobalData company details:", error);
      setDetailError(error.message || "Unable to load company details.");
    } finally {
      setDetailLoading(false);
    }
  }, [encodedCompanyName, getApiJson]);

  const loadDrugs = useCallback(async () => {
    setDrugsLoading(true);
    setDrugsError("");

    try {
      const payload = await getApiJson(
        `/api/globaldata/company-drugs?companyName=${encodedCompanyName}&page=${drugPage}&size=${DRUG_PAGE_SIZE}&sortBy=drugName&sortDirection=asc`
      );
      setDrugsPayload(payload);
    } catch (error) {
      console.error("Unable to load GlobalData company drugs:", error);
      setDrugsError(error.message || "Unable to load company drugs.");
    } finally {
      setDrugsLoading(false);
    }
  }, [drugPage, encodedCompanyName, getApiJson]);

  useEffect(() => {
    if (!expanded) return;
    loadDetails();
  }, [expanded, loadDetails]);

  useEffect(() => {
    if (!expanded || activeTab !== "drugs") return;
    loadDrugs();
  }, [activeTab, expanded, loadDrugs]);

  const handleCardToggle = () => {
    setExpanded((current) => {
      const nextExpanded = !current;
      if (!nextExpanded) {
        setActiveTab("details");
        setDrugPage(0);
      }
      return nextExpanded;
    });
  };

  const toggleContacted = async (event) => {
    event?.stopPropagation?.();
    if (!postJson || !deleteJson || contactedLoading) {
      return;
    }

    setContactedLoading(true);
    setContactedError("");

    try {
      if (isContacted) {
        await deleteJson(
          `/api/leads/lead-context/${LEAD_CONTEXTS.GLOBALDATA}/company/${encodedCompanyName}/contacted`
        );
      } else {
        await postJson(
          `/api/leads/lead-context/${LEAD_CONTEXTS.GLOBALDATA}/company/${encodedCompanyName}/contacted`
        );
      }

      onContactedChanged?.(company.companyName, !isContacted);
    } catch (error) {
      console.error("Unable to update contacted status:", error);
      setContactedError(error.message || "Unable to update contacted status.");
    } finally {
      setContactedLoading(false);
    }
  };

  const totalDrugPages = drugsPayload
    ? Math.max(1, Math.ceil((drugsPayload.total || 0) / (drugsPayload.size || DRUG_PAGE_SIZE)))
    : 1;
  const companyUrl = normalizeExternalUrl(detailPayload?.url);
  const activePipelineSummary = drugsPayload?.pipelineSummary || null;
  const firstInactiveDrugIndex =
    drugsPayload?.items?.findIndex((drug) => !drug.active) ?? -1;

  const detailFields = detailPayload
    ? [
        ["Highest Development Stage", formatValue(company.companyHighestDevelopmentStage)],
        ["Headquarters", formatValue(company.headquarters)],
        ["Entity Type", formatValue(company.entityType)],
        ["Relationship Type", formatValue(company.relationshipType)],
        ["CRO Tier", formatValue(detailPayload.croTier)],
        ["CRO Score", formatScore(detailPayload.croScore)],
        ["CDMO Tier", formatValue(detailPayload.cdmoTier)],
        ["CDMO Score", formatScore(detailPayload.cdmoScore)],
        ["Unique Linked Drugs", formatValue(detailPayload.uniqueDrugCount)],
        ["Company Drug Count", formatValue(company.companyDrugCount)],
        ["Company ID", formatValue(detailPayload.companyId)],
        ["Employees", formatValue(detailPayload.numberOfEmployees)],
        ["Annual Revenue", formatCurrency(detailPayload.annualRevenueUsdM)],
        [
          "Capital Raised (Past 3 Years)",
          formatCurrency(detailPayload.capitalRaisedPast3YearsUsdM),
        ],
        [
          "Grants Received (Past 3 Years)",
          formatCurrency(detailPayload.grantsReceivedPast3YearsUsdM),
        ],
      ]
    : [];

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        borderRadius: 3,
        borderLeft: company.matchedToCrunchbase
          ? "6px solid #4caf50"
          : "6px solid #cbd5e1",
        transition: "all 0.25s ease",
        boxShadow: expanded
          ? "0px 4px 14px rgba(0,0,0,0.18)"
          : "0px 2px 6px rgba(0,0,0,0.1)",
        ":hover": {
          boxShadow: "0px 6px 18px rgba(0,0,0,0.2)",
          transform: "scale(1.01)",
        },
      }}
    >
      <CardActionArea onClick={handleCardToggle}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, minWidth: 0, flex: "1 1 260px" }}>
              {company.companyName}
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 0.75,
                flexWrap: "wrap",
                justifyContent: { xs: "flex-start", sm: "flex-end" },
                flex: "1 1 360px",
              }}
            >
              {company.matchedToCrunchbase && (
                <Chip
                  label="Crunchbase"
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenCrunchbaseMatch?.(matchedCrunchbaseCompanyName);
                  }}
                  sx={{
                    color: "#166534",
                    backgroundColor: "#f0fdf4",
                    border: "1px solid rgba(34, 197, 94, 0.35)",
                    cursor: "pointer",
                  }}
                />
              )}
              {hasMeaningfulValue(company.croTier) && (
                <Chip
                  label={`CRO · ${company.croTier} · ${formatScore(company.croScore)}`}
                  size="small"
                  sx={getTierChipSx(company.croTier)}
                />
              )}
              {hasMeaningfulValue(company.cdmoTier) && (
                <Chip
                  label={`CDMO · ${company.cdmoTier} · ${formatScore(company.cdmoScore)}`}
                  size="small"
                  sx={getTierChipSx(company.cdmoTier)}
                />
              )}
              {(company.matchedToClient ||
                company.clientLifecycleStatus === "EXISTING_CLIENT" ||
                company.clientLifecycleStatus === "CONVERTED_CLIENT" ||
                company.clientLifecycleStatus === "NOT_CLIENT_ANYMORE") && (
                <Chip
                  label={
                    company.clientLifecycleStatus === "CONVERTED_CLIENT"
                      ? "Converted +"
                      : company.clientLifecycleStatus === "NOT_CLIENT_ANYMORE"
                      ? "Not client anymore"
                      : "Existing Client"
                  }
                  size="small"
                  sx={{
                    color:
                      company.clientLifecycleStatus === "NOT_CLIENT_ANYMORE"
                        ? "#991b1b"
                        : company.clientLifecycleStatus === "CONVERTED_CLIENT"
                        ? "#075985"
                        : "#9a3412",
                    backgroundColor:
                      company.clientLifecycleStatus === "NOT_CLIENT_ANYMORE"
                        ? "#fef2f2"
                        : company.clientLifecycleStatus === "CONVERTED_CLIENT"
                        ? "#f0f9ff"
                        : "#fff7ed",
                    border:
                      company.clientLifecycleStatus === "NOT_CLIENT_ANYMORE"
                        ? "1px solid rgba(239, 68, 68, 0.35)"
                        : company.clientLifecycleStatus === "CONVERTED_CLIENT"
                        ? "1px solid rgba(14, 165, 233, 0.28)"
                        : "1px solid rgba(249, 115, 22, 0.35)",
                  }}
                />
              )}
              {isContacted && (
                <Chip
                  label="Contacted"
                  size="small"
                  sx={{
                    color: "#9f1239",
                    backgroundColor: "#fff1f2",
                    border: "1px solid rgba(244, 63, 94, 0.28)",
                  }}
                />
              )}
              {company.matchedToCompanyHouse && (
                <Chip
                  label="Company House"
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenCompanyHouseMatch?.(matchedCompanyHouseCompanyName);
                  }}
                  sx={{
                    color: "#0f766e",
                    backgroundColor: "#ecfeff",
                    border: "1px solid rgba(13, 148, 136, 0.35)",
                    cursor: "pointer",
                  }}
                />
              )}
            </Box>
          </Box>

          <Typography
            variant="body1"
            sx={{
              mt: 1,
              fontStyle: "italic",
              fontSize: "1rem",
            }}
          >
            {formatValue(company.overview)}
          </Typography>
        </CardContent>
      </CardActionArea>

      <Collapse in={expanded} timeout={{ enter: 400, exit: 250 }} unmountOnExit>
        <CardContent sx={{ backgroundColor: "#fafafa" }}>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                lg: "minmax(0, 1.55fr) minmax(320px, 0.95fr)",
              },
              alignItems: "start",
              mt: 0.25,
              pt: 1.5,
              borderTop: "1px solid rgba(15, 23, 42, 0.08)",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gap: 1.25,
              }}
            >
              <Box
                sx={{
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  borderRadius: 2,
                  backgroundColor: "#ffffff",
                  overflow: "hidden",
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={(_, nextValue) => setActiveTab(nextValue)}
                  variant="fullWidth"
                  sx={{
                    px: 1,
                    pt: 1,
                    borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.84rem",
                      minHeight: 42,
                    },
                  }}
                >
                  <Tab value="details" label="Company" />
                  <Tab value="drugs" label="Drugs" />
                </Tabs>

                <Box sx={{ p: 1.25, display: "grid", gap: 1 }}>
                  {activeTab === "details" ? (
                    detailLoading && !detailPayload ? (
                      <Box sx={{ py: 4, textAlign: "center" }}>
                        <CircularProgress size={28} />
                      </Box>
                    ) : detailError ? (
                      <Alert severity="error">{detailError}</Alert>
                    ) : detailPayload ? (
                      <>
                        <Box sx={{ display: "grid", gap: 0.65 }}>
                          {detailFields.map(([label, value]) => (
                            <DetailTextRow key={label} label={label} value={value} />
                          ))}
                        </Box>

                        {companyUrl && (
                          <Typography sx={{ fontSize: "0.93rem", color: "#334155" }}>
                            <strong>Company URL:</strong>{" "}
                            <a href={companyUrl} target="_blank" rel="noreferrer">
                              {detailPayload.url}
                            </a>
                          </Typography>
                        )}

                        {company.matchedToCrunchbase && (
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Button
                              variant="outlined"
                              startIcon={<LaunchOutlinedIcon fontSize="small" />}
                              onClick={(event) => {
                                event.stopPropagation();
                                onOpenCrunchbaseMatch(matchedCrunchbaseCompanyName);
                              }}
                              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999 }}
                            >
                              Open Crunchbase match
                            </Button>
                          </Box>
                        )}
                        {company.matchedToCompanyHouse && (
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Button
                              variant="outlined"
                              startIcon={<LaunchOutlinedIcon fontSize="small" />}
                              onClick={(event) => {
                                event.stopPropagation();
                                onOpenCompanyHouseMatch?.(matchedCompanyHouseCompanyName);
                              }}
                              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999 }}
                            >
                              Open Company House match
                            </Button>
                          </Box>
                        )}

                      </>
                    ) : null
                  ) : (
                    <>
                      {activePipelineSummary && (
                        <Box
                          sx={{
                            display: "grid",
                            gap: 0.75,
                            p: 1.1,
                            borderRadius: 2,
                            border: "1px solid rgba(15, 23, 42, 0.08)",
                            backgroundColor: "#ffffff",
                          }}
                        >
                          <Typography sx={{ fontSize: "0.9rem", color: "#334155" }}>
                            <strong>Active pipeline:</strong>{" "}
                            {formatCount(activePipelineSummary.activeAssetCount)} assets
                          </Typography>
                          <Typography sx={{ fontSize: "0.9rem", color: "#334155" }}>
                            <strong>Stage mix:</strong>{" "}
                            {formatBucketSummary(activePipelineSummary.activeStageDistribution)}
                          </Typography>
                          <Typography sx={{ fontSize: "0.9rem", color: "#334155" }}>
                            <strong>Molecule type:</strong>{" "}
                            {formatBucketSummary(activePipelineSummary.activeMoleculeTypeDistribution)}
                          </Typography>
                        </Box>
                      )}

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                          {drugsPayload ? `${drugsPayload.total} total drugs` : "Company drugs"}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={drugsLoading || drugPage === 0}
                            onClick={() => setDrugPage((current) => Math.max(0, current - 1))}
                            sx={{ textTransform: "none", fontWeight: 700 }}
                          >
                            Previous
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={drugsLoading || drugPage + 1 >= totalDrugPages}
                            onClick={() => setDrugPage((current) => current + 1)}
                            sx={{ textTransform: "none", fontWeight: 700 }}
                          >
                            Next
                          </Button>
                        </Box>
                      </Box>

                      <Typography sx={{ fontSize: "0.86rem", color: "#64748b" }}>
                        Page {drugPage + 1} of {totalDrugPages}
                      </Typography>

                      {drugsLoading && !drugsPayload ? (
                        <Box sx={{ py: 4, textAlign: "center" }}>
                          <CircularProgress size={28} />
                        </Box>
                      ) : drugsError ? (
                        <Alert severity="error">{drugsError}</Alert>
                      ) : drugsPayload?.items?.length ? (
                        <Box sx={{ display: "grid", gap: 1 }}>
                          {drugsPayload.items.map((drug, index) => (
                            <Box key={`${drug.drugName || "drug"}-${index}`} sx={{ display: "grid", gap: 1 }}>
                              {firstInactiveDrugIndex === index && (
                                <Box
                                  sx={{
                                    mt: index === 0 ? 0 : 0.5,
                                    px: 0.25,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "0.78rem",
                                      fontWeight: 700,
                                      letterSpacing: "0.04em",
                                      textTransform: "uppercase",
                                      color: "#64748b",
                                    }}
                                  >
                                    Inactive assets
                                  </Typography>
                                </Box>
                              )}
                              <DrugListItem drug={drug} />
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography sx={{ color: "#64748b" }}>
                          No drugs returned for this company.
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "start" }}>
                <LeadAssignmentPanel
                  companyName={company.companyName}
                  assignment={assignment}
                  canManageAssignments={canManageAssignments}
                  assignableUsers={assignableUsers}
                  assignableUsersLoading={assignableUsersLoading}
                  postJson={postJson}
                  deleteJson={deleteJson}
                  onAssignmentsChanged={onAssignmentsChanged}
                />
                <ContactedStatusControl
                  contacted={isContacted}
                  loading={contactedLoading}
                  error={contactedError}
                  onToggle={toggleContacted}
                />
              </Box>
            </Box>

            <CommentsPanel
              expanded={expanded}
              leadContext={LEAD_CONTEXTS.GLOBALDATA}
              organizationName={company.companyName}
              getApiJson={getApiJson}
              postJson={postJson}
            />
          </Box>
        </CardContent>
      </Collapse>
    </Card>
  );
}
